import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { residentsApiService, Resident, ResidentCreateDTO, ResidentUpdateDTO, ResidentFilters } from '../api/residents-api';
import { CondominiumResident } from '../types/resident.types';

// Estado inicial
interface ResidentsState {
  residents: CondominiumResident[];
  currentResident: CondominiumResident | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: ResidentFilters;
  stats: {
    totalResidents: number;
    activeResidents: number;
    inactiveResidents: number;
    occupied: number;
    vacant: number;
    residentsByTower: Record<string, number>;
    residentsByOccupancyStatus: Record<string, number>;
  } | null;
}

const initialState: ResidentsState = {
  residents: [],
  currentResident: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {},
  stats: null,
};

// Función para convertir Resident de API a CondominiumResident
const mapResidentToCondominium = (resident: Resident): CondominiumResident => ({
  id: resident.id,
  name: resident.name,
  email: resident.email,
  phone: resident.phone || '',
  unit: resident.apartment,
  apartment: resident.apartment,
  tower: resident.tower,
  status: resident.status as 'active' | 'inactive',
  occupancyStatus: resident.occupancyStatus as 'owner' | 'renter',
  moveInDate: resident.moveInDate,
  vehicles: resident.vehicles || 0,
});

// Async Thunks
export const fetchResidents = createAsyncThunk(
  'residents/fetchResidents',
  async (filters: ResidentFilters = {}, { rejectWithValue }) => {
    try {
      const response = await residentsApiService.getResidents(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar residentes'
      );
    }
  }
);

export const fetchResident = createAsyncThunk(
  'residents/fetchResident',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await residentsApiService.getResidentById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar residente'
      );
    }
  }
);

export const createResident = createAsyncThunk(
  'residents/createResident',
  async (data: ResidentCreateDTO, { rejectWithValue }) => {
    try {
      const response = await residentsApiService.createResident(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear residente'
      );
    }
  }
);

export const updateResident = createAsyncThunk(
  'residents/updateResident',
  async (
    { id, data }: { id: string; data: ResidentUpdateDTO },
    { rejectWithValue }
  ) => {
    try {
      const response = await residentsApiService.updateResident(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar residente'
      );
    }
  }
);

export const deleteResident = createAsyncThunk(
  'residents/deleteResident',
  async (id: string, { rejectWithValue }) => {
    try {
      await residentsApiService.deleteResident(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar residente'
      );
    }
  }
);

export const toggleResidentStatus = createAsyncThunk(
  'residents/toggleResidentStatus',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await residentsApiService.toggleResidentStatus(id);
      return mapResidentToCondominium(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cambiar estado del residente'
      );
    }
  }
);

export const searchResidents = createAsyncThunk(
  'residents/searchResidents',
  async (searchTerm: string, { rejectWithValue }) => {
    try {
      const response = await residentsApiService.getResidents({
        search: searchTerm,
        limit: 50, // Limitar resultados para búsqueda
        page: 1,
      });
      return response.residents || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al buscar residentes'
      );
    }
  }
);

export const fetchResidentStats = createAsyncThunk(
  'residents/fetchResidentStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await residentsApiService.getResidentStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar estadísticas'
      );
    }
  }
);

// Slice
const residentsSlice = createSlice({
  name: 'residents',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<ResidentFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: state => {
      state.filters = {};
    },
    setCurrentResident: (state, action: PayloadAction<CondominiumResident | null>) => {
      state.currentResident = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination.page = action.payload.page;
      state.pagination.limit = action.payload.limit;
    },
  },
  extraReducers: builder => {
    // Fetch residents
    builder
      .addCase(fetchResidents.pending, state => {
        // Solo mostrar loading si no hay residentes cargados (evita glitches en actualizaciones)
        if (state.residents.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchResidents.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.residents = (payload.residents || []).map(mapResidentToCondominium);
        
        // Solo actualizar paginación si los valores realmente cambiaron
        const newPage = payload.page || 1;
        const newLimit = payload.limit || 10;
        const newTotal = payload.total || 0;
        const newTotalPages = payload.totalPages || 1;
        
        // Actualizar solo si hay cambios para evitar re-renders innecesarios
        if (
          state.pagination.page !== newPage ||
          state.pagination.limit !== newLimit ||
          state.pagination.total !== newTotal ||
          state.pagination.totalPages !== newTotalPages
        ) {
          state.pagination = {
            page: newPage,
            limit: newLimit,
            total: newTotal,
            totalPages: newTotalPages,
          };
        }
      })
      .addCase(fetchResidents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch resident
    builder
      .addCase(fetchResident.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchResident.fulfilled, (state, action) => {
        state.loading = false;
        state.currentResident = mapResidentToCondominium(action.payload as Resident);
      })
      .addCase(fetchResident.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create resident
    builder
      .addCase(createResident.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createResident.fulfilled, (state, action) => {
        state.loading = false;
        const newResident = mapResidentToCondominium(action.payload as Resident);
        state.residents.unshift(newResident);
        state.pagination.total += 1;
      })
      .addCase(createResident.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update resident
    builder
      .addCase(updateResident.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateResident.fulfilled, (state, action) => {
        state.loading = false;
        const updatedResident = mapResidentToCondominium(action.payload as Resident);
        const index = state.residents.findIndex(
          resident => resident.id === updatedResident.id
        );
        if (index !== -1) {
          state.residents[index] = updatedResident;
        }
        if (state.currentResident?.id === updatedResident.id) {
          state.currentResident = updatedResident;
        }
      })
      .addCase(updateResident.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete resident
    builder
      .addCase(deleteResident.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteResident.fulfilled, (state, action) => {
        state.loading = false;
        state.residents = state.residents.filter(
          resident => resident.id !== action.payload
        );
        state.pagination.total -= 1;
        if (state.currentResident?.id === action.payload) {
          state.currentResident = null;
        }
      })
      .addCase(deleteResident.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Toggle resident status
    builder
      .addCase(toggleResidentStatus.pending, state => {
        // No cambiar loading global para evitar glitches en la tabla
        state.error = null;
      })
      .addCase(toggleResidentStatus.fulfilled, (state, action) => {
        // No cambiar loading global - solo actualizar el residente específico
        const updatedResident = action.payload;
        const index = state.residents.findIndex(
          resident => resident.id === updatedResident.id
        );
        if (index !== -1) {
          // Guardar el estado anterior antes de actualizar
          const oldStatus = state.residents[index].status;
          const newStatus = updatedResident.status;
          
          // Actualizar el residente en la lista
          state.residents[index] = updatedResident;
          
          // Actualizar estadísticas si están cargadas
          if (state.stats && oldStatus !== newStatus) {
            if (oldStatus === 'active') {
              state.stats.activeResidents -= 1;
              state.stats.inactiveResidents += 1;
            } else {
              state.stats.activeResidents += 1;
              state.stats.inactiveResidents -= 1;
            }
          }
        }
        
        // Actualizar currentResident si es el que se modificó
        if (state.currentResident?.id === updatedResident.id) {
          state.currentResident = updatedResident;
        }
      })
      .addCase(toggleResidentStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch resident stats
    builder
      .addCase(fetchResidentStats.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchResidentStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload as any;
      })
      .addCase(fetchResidentStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentResident,
  clearError,
  setPagination,
} = residentsSlice.actions;

// Selectors
export const selectResidents = (state: { residents: ResidentsState }) => state.residents.residents;
export const selectCurrentResident = (state: { residents: ResidentsState }) => state.residents.currentResident;
export const selectResidentsLoading = (state: { residents: ResidentsState }) => state.residents.loading;
export const selectResidentsError = (state: { residents: ResidentsState }) => state.residents.error;
export const selectResidentsPagination = (state: { residents: ResidentsState }) => state.residents.pagination;
export const selectResidentsFilters = (state: { residents: ResidentsState }) => state.residents.filters;
export const selectResidentsStats = (state: { residents: ResidentsState }) => state.residents.stats;

export default residentsSlice.reducer;



